import type {
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { HttpStatusCode } from '@angular/common/http';
import { inject } from '@angular/core';
import type { OnyxHttpErrorResponse } from '@onyx/angular';
import { OnyxToastService } from '@onyx/angular';
import type { Observable } from 'rxjs';
import { catchError, finalize, shareReplay, switchMap, take } from 'rxjs';
import { environment } from '../../../../config/environment';
import { AUTH_INTERCEPTOR_NAMESPACE_HEADER } from '../constants/auth-interceptor-namespace-header';
import { AuthService } from '../services/auth.service';
import { AuthStore } from '../stores/auth.store';

let refreshToken$: Observable<string> | null = null;

export const authInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
) => {
  const store = inject(AuthStore);
  const authService = inject(AuthService);
  const toastService = inject(OnyxToastService);

  request = addNamespace(request, store.companyId());

  const isAuth = /\/(login$|token\/)/.test(request.url);
  const token = store.token();
  if (!isAuth && token) request = addAuthorizationHeader(request, token);

  const refreshRequest$ = (request: HttpRequest<unknown>) =>
    refreshToken$!.pipe(
      switchMap((token) => {
        request = addAuthorizationHeader(request, token);
        return next(request);
      }),
    );
  if (refreshToken$ && !isAuth) return refreshRequest$(request);

  return next(request).pipe(
    catchError((response: OnyxHttpErrorResponse) => {
      const logout$ = () => {
        authService.logout({ redirect: true });
        toastService.showError({
          message: 'auth.sessionExpired',
          keepOnNavigation: true,
        });

        response.handled = true;
        throw response;
      };

      if (isAuth) throw response;

      const isUnauthorized = [
        HttpStatusCode.Unauthorized,
        HttpStatusCode.Forbidden,
      ].includes(response.status);
      if (!isUnauthorized) throw response;

      refreshToken$ ??= authService.refreshToken().pipe(
        catchError(() => logout$()),
        finalize(() => (refreshToken$ = null)),
        shareReplay(1),
        take(1),
      );

      return refreshRequest$(request);
    }),
  );
};

const addNamespace = (
  request: HttpRequest<unknown>,
  companyId: string | null,
): HttpRequest<unknown> => {
  const url = getNamespacedUrl(
    request.url,
    companyId,
    request.headers.get(AUTH_INTERCEPTOR_NAMESPACE_HEADER),
  );
  const headers = request.headers.delete(AUTH_INTERCEPTOR_NAMESPACE_HEADER);

  return request.clone({ url, headers });
};

export const getNamespacedUrl = (
  url: string,
  companyId: string | null,
  namespace: string | null = null,
): string => {
  if (
    !url.startsWith(environment.apiUrl) &&
    !url.startsWith(environment.mercureUrl)
  ) {
    return url;
  }

  namespace ??= companyId ?? environment.publicApiNamespace;

  const result = new URL(url);
  result.hostname = `${namespace}.${result.hostname}`;

  if (
    ['test', 'local'].includes(environment.type) &&
    namespace === environment.publicApiNamespace
  ) {
    result.port = '81';
  }

  return result.href;
};

const addAuthorizationHeader = (
  request: HttpRequest<unknown>,
  token: string,
): HttpRequest<unknown> => {
  return request.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`,
    },
  });
};
